import { WalletField } from '../../../NewDappPage/components/WalletField';
import type { TaskEither } from 'fp-ts/lib/TaskEither';
import type { PreviewUploadsController } from '../../../../hooks/useDappFileHandlers';
import type { UpdateDappFormData as UpdateDappDataType } from '../../../../lib/schemas/formSchemas';
import type { TypedError } from '../../../../lib/typedError';
import { cn } from '../../../../lib/utils';
import { tabId, tabPanelId } from '../../../common/TabNavigation';
import * as Form from '../../../Form';
import { EditorsChoicePreview } from '../../../shared/EditorsChoicePreview';
import { DappPreviewPanelWithUrls } from '../../DappPreviewPanelWithUrls';
import { FORM_ID, UpdateDappFormData } from '../constants';
import { AdditionalInfoSection } from '../sections/AdditionalInfoSection';
import { BasicDetailsSection } from '../sections/BasicDetailsSection';
import { EditorsChoiceSection } from '../sections/EditorsChoiceSection';
import { LegalComplianceSection } from '../sections/LegalComplianceSection';
import type {
  DashboardDetailsInputChangeEvent,
  FormState,
  PreviewData,
} from '../types';
import { FormValidityObserver } from './FormValidityObserver';

export type StoreListingTab = 'details' | 'editors-choice';
export const STORE_LISTING_TABS_ID = 'store-listing';

interface DashboardDetailsFormProps {
  formKey: number;
  activeTab: StoreListingTab;
  formData: FormState;
  previewPanelData: PreviewData;
  previewUploads: PreviewUploadsController;
  resolvedDappIconUrl: string | null;
  resolvedBannerUrl: string | null;
  resolvedPreviewUrls: (string | null)[];
  resolvedEditorsChoiceGraphicUrl: string | null;
  existingPreviewUrls: (string | null)[];
  onValidityChange: (isValid: boolean) => void;
  onInputChange: (event: DashboardDetailsInputChangeEvent) => void;
  onSingleFileChange: (
    fieldName: 'dappIcon' | 'banner' | 'editorsChoiceGraphic',
    file: File | null,
  ) => Promise<void>;
  onRemoveExistingDappIcon: () => void;
  onRemoveExistingBanner: () => void;
  onRemoveExistingPreview: (index: number) => void;
  onRemoveExistingGraphic: () => void;
  onSubmit: (
    input: TaskEither<TypedError<'InvalidFormData'>, UpdateDappDataType>,
  ) => TaskEither<unknown, unknown>;
  /** Save Draft / Publish in flight: block edits so the completion never overwrites newer input. */
  isBusy?: boolean;
  showWalletField?: boolean;
}

/**
 * Both tab panels stay mounted (toggled with `hidden`) so the single form
 * always submits every field and constraint validation covers both tabs.
 * The CSS `animate-in` restarts whenever a panel leaves `display: none`, so
 * the newly shown panel fades in (120ms) with no unmount and no empty frame.
 */
const PANEL_FADE_CLASS =
  'animate-in fade-in ease-out animation-duration-120 fill-mode-both';

export function DashboardDetailsForm({
  formKey,
  activeTab,
  formData,
  previewPanelData,
  previewUploads,
  resolvedDappIconUrl,
  resolvedBannerUrl,
  resolvedPreviewUrls,
  resolvedEditorsChoiceGraphicUrl,
  existingPreviewUrls,
  onValidityChange,
  onInputChange,
  onSingleFileChange,
  onRemoveExistingDappIcon,
  onRemoveExistingBanner,
  onRemoveExistingPreview,
  onRemoveExistingGraphic,
  onSubmit,
  isBusy = false,
  showWalletField = false,
}: DashboardDetailsFormProps) {
  const previewCount = formData.dappPreviews.filter(
    (file, index) => file || existingPreviewUrls[index],
  ).length;

  return (
    <Form.Root
      key={formKey}
      id={FORM_ID}
      formDataShape={UpdateDappFormData}
      onSubmitData={onSubmit}
      className="flex flex-col gap-12 px-5 py-8 sm:px-[60px] sm:py-12 lg:flex-row lg:items-start lg:justify-between"
      validityKey={JSON.stringify(formData)}
    >
      <FormValidityObserver
        onValidityChange={onValidityChange}
        hasDappIcon={Boolean(formData.dappIcon || resolvedDappIconUrl)}
        hasPreviewImages={previewCount > 0}
      />

      <div
        aria-busy={isBusy || undefined}
        className={cn(
          'flex w-full max-w-[640px] flex-col gap-10',
          isBusy && 'pointer-events-none select-none',
        )}
      >
        <div
          id={tabPanelId(STORE_LISTING_TABS_ID, 'details')}
          aria-labelledby={tabId(STORE_LISTING_TABS_ID, 'details')}
          role="tabpanel"
          hidden={activeTab !== 'details'}
          className={cn('flex flex-col gap-10', PANEL_FADE_CLASS)}
        >
          <div className="flex flex-col gap-2">
            <div
              role="heading"
              aria-level={2}
              className="text-4xl font-medium leading-10 text-white"
            >
              Create your dApp Store Listing
            </div>
            <p className="text-sm text-seeker-400">
              This is the information users will see in the Solana Mobile dApp
              Store.
            </p>
          </div>

          {showWalletField && <WalletField />}
          <BasicDetailsSection
            formData={formData}
            previewUploads={previewUploads}
            resolvedDappIconUrl={resolvedDappIconUrl}
            resolvedBannerUrl={resolvedBannerUrl}
            resolvedPreviewUrls={resolvedPreviewUrls}
            existingPreviewUrls={existingPreviewUrls}
            onInputChange={onInputChange}
            onSingleFileChange={onSingleFileChange}
            onRemoveExistingDappIcon={onRemoveExistingDappIcon}
            onRemoveExistingBanner={onRemoveExistingBanner}
            onRemoveExistingPreview={onRemoveExistingPreview}
          />

          <AdditionalInfoSection
            formData={formData}
            onInputChange={onInputChange}
          />

          <LegalComplianceSection
            formData={formData}
            onInputChange={onInputChange}
          />
        </div>

        <div
          id={tabPanelId(STORE_LISTING_TABS_ID, 'editors-choice')}
          aria-labelledby={tabId(STORE_LISTING_TABS_ID, 'editors-choice')}
          role="tabpanel"
          hidden={activeTab !== 'editors-choice'}
          className={PANEL_FADE_CLASS}
        >
          <EditorsChoiceSection
            formData={formData}
            resolvedEditorsChoiceGraphicUrl={resolvedEditorsChoiceGraphicUrl}
            onInputChange={onInputChange}
            onSingleFileChange={onSingleFileChange}
            onRemoveExistingGraphic={onRemoveExistingGraphic}
          />
        </div>

        <Form.Error className="mt-1 text-right" />
      </div>

      <div className="w-full shrink-0 lg:sticky lg:top-8 lg:w-[300px]">
        {/* Keyed so the phone swaps with the same fade as the panel. */}
        <div key={activeTab} className={PANEL_FADE_CLASS}>
          {activeTab === 'details' ? (
            <DappPreviewPanelWithUrls
              formData={previewPanelData}
              maxPreviews={formData.dappPreviews.length}
              previewDimensions={previewUploads.previewDimensions}
            />
          ) : (
            <EditorsChoicePreview
              dappName={formData.dappName}
              subtitle={formData.subtitle}
              headline={formData.editorsChoiceHeadline}
              tag={formData.editorsChoiceTag}
              iconFile={formData.dappIcon}
              existingIconUrl={resolvedDappIconUrl}
              graphicFile={formData.editorsChoiceGraphic}
              existingGraphicUrl={resolvedEditorsChoiceGraphicUrl}
            />
          )}
        </div>
      </div>
    </Form.Root>
  );
}
